import { useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { ITipsFeedbackFilters, ITipsFeedbackMetaAnalysis } from '../../../../interfaces/tips-feedback/ITipsFeedback';
import { useGetTipsFeedbackMetaAnalysisMutation, useGetTipsFeedbackMutation } from '../../../../store/chatbox/chatboxApi';
import { feedbackErrorMessage } from '../utils';

type Suggestions = ITipsFeedbackMetaAnalysis['suggestions'];
const suggestionSections: { key: keyof Suggestions; title: string }[] = [
    { key: 'promptAmendments', title: 'Prompt' },
    { key: 'newFeatures', title: 'New feature' },
    { key: 'newAreasOfFocus', title: 'New area of focus' },
];

const SuggestionEditor = ({ title, values, onChange }: { title: string; values: string[]; onChange: (values: string[]) => void }) => {
    const [editing, setEditing] = useState(false);
    return (
        <div>
            <div className='flex items-center justify-between gap-3'>
                <h4 className='text-sm font-medium text-gray-700 dark:text-gray-200'>{title}</h4>
                <button type='button' aria-label={`${editing ? 'Finish editing' : 'Edit'} ${title} suggestions`} onClick={() => setEditing(!editing)}
                    className='flex h-9 min-w-[36px] items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'>
                    {editing ? <span className='text-xs font-semibold'>Done</span> : <svg aria-hidden='true' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6'><path d='m16 3 5 5-12 12-6 1 1-6L16 3Zm-3 3 5 5M4 15l5 5' /></svg>}
                </button>
            </div>
            {editing ? <label className='block text-xs text-gray-500 dark:text-gray-400'>One suggestion per line
                <textarea aria-label={`${title} suggestions`} rows={4} value={values.join('\n')} onChange={(event) => onChange(event.target.value.split('\n'))}
                    className='mt-2 w-full rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white' />
            </label> : values.some((value) => value.trim()) ? <ul className='mt-1 space-y-2 text-sm text-gray-600 dark:text-gray-300'>
                {values.filter((value) => value.trim()).map((value, index) => <li key={index} className='whitespace-pre-wrap break-words'>{value}</li>)}
            </ul> : <p className='mt-1 text-sm text-gray-400'>No suggestions yet</p>}
        </div>
    );
};

const TipsFeedbackAnalysis = ({ filters, feedbackId }: { filters: ITipsFeedbackFilters; feedbackId?: string }) => {
    const [getAnalysis] = useGetTipsFeedbackMetaAnalysisMutation();
    const [getFeedback] = useGetTipsFeedbackMutation();
    const [analysis, setAnalysis] = useState<ITipsFeedbackMetaAnalysis>();
    const [selectedTheme, setSelectedTheme] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [edited, setEdited] = useState(false);
    const mounted = useRef(true);
    const busy = useRef(false);

    useLayoutEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const exportAnalysis = (result: ITipsFeedbackMetaAnalysis, hasEdits = edited) => {
        const workbook = XLSX.utils.book_new();
        const metadata = XLSX.utils.json_to_sheet([{
            Scope: feedbackId ? 'Individual feedback' : 'Filtered feedback',
            'Feedback ID': feedbackId || '',
            Filters: JSON.stringify(feedbackId ? { type: filters.type || 'tips', search: feedbackId } : filters),
            'Generated at': result.generatedAt,
            'Records analyzed': result.analyzedFeedbackCount,
            Truncated: result.truncated ? 'Yes' : 'No',
            'Suggestions edited locally': hasEdits ? 'Yes' : 'No',
        }]);
        const themes = XLSX.utils.json_to_sheet(result.feedbackThemes.map((theme) => ({ Theme: theme.theme, Count: theme.count, Summary: theme.summary })));
        const suggestions = XLSX.utils.json_to_sheet(suggestionSections.flatMap(({ key, title }) =>
            (result.suggestions[key] || []).filter((value) => value.trim()).map((value) => ({ Area: title, Suggestion: value.trim() }))));
        themes['!cols'] = [{ wch: 40 }, { wch: 12 }, { wch: 90 }];
        suggestions['!cols'] = [{ wch: 24 }, { wch: 100 }];
        XLSX.utils.book_append_sheet(workbook, metadata, 'Analysis scope');
        XLSX.utils.book_append_sheet(workbook, themes, 'Feedback themes');
        XLSX.utils.book_append_sheet(workbook, suggestions, 'Suggestions');
        XLSX.writeFile(workbook, `${feedbackId ? `feedback-${feedbackId}` : 'feedback-meta'}-analysis-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const generateAnalysis = async (download = false) => {
        if (busy.current) return;
        busy.current = true;
        setLoading(true);
        setError('');
        setAnalysis(undefined);
        setSelectedTheme('');
        try {
            const scope = feedbackId ? { type: filters.type || 'tips' as const, search: feedbackId } : filters;
            if (feedbackId) {
                if (!/^[a-f\d]{24}$/i.test(feedbackId)) throw new Error('This feedback has an invalid ID and cannot be analyzed.');
                const match = await getFeedback({ pageNumber: 1, pageSize: 2, sort: 'date,desc', data: scope }).unwrap();
                if (!mounted.current) return;
                if (match.elements.length !== 1 || match.elements[0]._id !== feedbackId || match.page?.totalElements !== 1 || match.page?.hasNextPage) {
                    throw new Error('This feedback could not be isolated for analysis. Please contact support.');
                }
            }
            const result = await getAnalysis({ sort: 'date,desc', data: scope }).unwrap();
            if (!mounted.current) return;
            if (feedbackId && (result.analyzedFeedbackCount !== 1 || result.truncated)) {
                throw new Error('The analysis included other feedback and was discarded. Please try again.');
            }
            setAnalysis(result);
            setSelectedTheme(result.feedbackThemes?.length ? '0' : '');
            setEdited(false);
            if (download) exportAnalysis(result, false);
        } catch (requestError) {
            if (mounted.current) setError(feedbackErrorMessage(requestError, 'Unable to load AI analysis. Please try again.'));
        } finally {
            busy.current = false;
            if (mounted.current) setLoading(false);
        }
    };

    const onExport = () => {
        if (!analysis) { generateAnalysis(true); return; }
        try { exportAnalysis(analysis); } catch (exportError) { setError(feedbackErrorMessage(exportError, 'Unable to export analysis. Please try again.')); }
    };
    const theme = selectedTheme === '' ? undefined : analysis?.feedbackThemes[Number(selectedTheme)];

    return (
        <section className='flex h-full min-h-0 flex-col' aria-label={feedbackId ? 'Individual feedback analysis' : 'AI feedback analysis'}>
            <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6'>
                <h3 className='text-base font-medium text-gray-800 dark:text-gray-100'>{feedbackId ? 'Feedback analysis' : 'Feedback'}</h3>
                <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>{feedbackId ? 'Analyze only this feedback and its original recommendation.' : 'Analyze up to 100 most recent feedback records using the applied filters.'}</p>
                {feedbackId && <p className='mt-1 break-all text-xs text-gray-400'>Feedback ID: {feedbackId}</p>}
                {loading && <p role='status' className='mt-4 text-sm text-gray-500 dark:text-gray-300'>Preparing AI analysis...</p>}
                {error && <p role='alert' className='mt-4 text-sm text-red-600 dark:text-red-400'>{error}</p>}
                {analysis && <div className='mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400'>
                    <p>{analysis.analyzedFeedbackCount} {analysis.analyzedFeedbackCount === 1 ? 'feedback record' : 'feedback records'} analyzed</p>
                    {moment(analysis.generatedAt).isValid() && <p>Generated {moment(analysis.generatedAt).format('DD MMM YYYY, HH:mm')}</p>}
                </div>}
                {analysis?.truncated && <p role='status' className='mt-3 text-sm text-amber-700 dark:text-amber-400'>This analysis is limited to the 100 most recent matching feedback records.</p>}
                <div className='mt-4 border border-gray-200 dark:border-gray-700'>
                    <div className='grid border-b border-gray-200 dark:border-gray-700 sm:grid-cols-[170px_minmax(0,1fr)]'>
                        <label htmlFor={`feedback-themes-${feedbackId || 'meta'}`} className='p-3 text-sm text-gray-500 dark:text-gray-400'>Feedback themes</label>
                        <div className='min-w-0 p-3 sm:min-h-[150px]'>
                            <select id={`feedback-themes-${feedbackId || 'meta'}`} value={selectedTheme} onChange={(event) => setSelectedTheme(event.target.value)} disabled={!analysis?.feedbackThemes?.length || loading}
                                className='min-h-[36px] w-full rounded-lg bg-transparent pr-2 text-sm text-gray-800 disabled:text-gray-400 dark:bg-bgColor-darkMode dark:text-gray-100'>
                                <option value=''>{analysis ? 'Select a theme' : 'No theme selected yet'}</option>
                                {analysis?.feedbackThemes?.map((entry, index) => <option key={index} value={index}>{entry.theme}</option>)}
                            </select>
                            {theme && <div className='mt-3 text-sm text-gray-700 dark:text-gray-300'><p className='whitespace-pre-wrap break-words'>{theme.summary}</p><p className='mt-2 text-xs text-gray-500'>{theme.count} {theme.count === 1 ? 'mention' : 'mentions'}</p></div>}
                            {analysis && !analysis.feedbackThemes?.length && <p className='mt-3 text-sm text-gray-400'>No feedback themes returned.</p>}
                        </div>
                    </div>
                    <div className='grid sm:grid-cols-[170px_minmax(0,1fr)]'>
                        <h4 className='p-3 text-sm font-normal text-gray-500 dark:text-gray-400'>Suggestions for enhancement</h4>
                        <div className='min-w-0 space-y-5 p-3 sm:border-l sm:border-gray-200 dark:sm:border-gray-700'>
                            {suggestionSections.map(({ key, title }) => analysis ? <SuggestionEditor key={`${analysis.generatedAt}:${key}`} title={title} values={analysis.suggestions[key] || []}
                                onChange={(values) => { setAnalysis({ ...analysis, suggestions: { ...analysis.suggestions, [key]: values } }); setEdited(true); }} /> : <div key={key}><h4 className='text-sm text-gray-700 dark:text-gray-200'>{title}</h4><p className='mt-2 text-sm text-gray-400'>No suggestions yet</p></div>)}
                        </div>
                    </div>
                </div>
                {analysis && <p className='mt-3 text-xs text-gray-500 dark:text-gray-400'>Suggestion edits are included in your export only.</p>}
            </div>
            <div className='flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-100 p-4 dark:border-gray-700 sm:px-6'>
                <button type='button' onClick={() => generateAnalysis()} disabled={loading} className='min-h-[44px] rounded-full border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200'>
                    {loading ? 'Analyzing...' : analysis ? 'Refresh analysis' : 'Analyze feedback'}
                </button>
                <button type='button' onClick={onExport} disabled={loading} className='min-h-[44px] rounded-full bg-[#00330f] px-6 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-black'>
                    {analysis ? 'Export analysis' : 'Analyse and Export'}
                </button>
            </div>
        </section>
    );
};

export default TipsFeedbackAnalysis;
